<template>
  <div class="flex items-center justify-center container">
    <div class="w-full sm:px-6 lg:w-2/5">
      <div
        class="text-center uppercase text-title font-bold pb-5 pt-[5rem] sm:pt-5"
      >
        Order Summary
      </div>
    </div>
  </div>
  <div class="flex items-center justify-center container">
    <div class="w-full sm:px-6 lg:w-2/5">
      <ticket-summary />
    </div>
  </div>

  <div class="sm:py-10 fixed-bottom">
    <div class="text-center px-5 sm:px-0">
      <div class="text-content mt-2" v-if="reservation && reservation.id">
        <div class="n3-checkbox">
          <label for="agree">
            <input
              type="checkbox"
              id="agree"
              checked="checked"
              v-model="agree"
            />
            <span class="checkmark"></span>
            By continuing
            <a
              href="/terms"
              target="_blank"
              class="text-n3yellow"
              @click.prevent="handleTermsAndCondition"
            >
              T&C </a
            >and
            <a href="/privacy" class="text-n3yellow" target="_blank">
              Privacy Policy</a
            >
            agreements apply
          </label>
        </div>
      </div>

      <default-button
        :disabled="isLoading || !(reservation && reservation.id) || !agree"
        :loading="isLoading"
        :label="isLoading ? 'Please wait...' : 'Pay Now'"
        classes="w-full sm:w-2/6 mt-10 sm:mt-0"
        @clicked="handlePayment()"
      />
    </div>
  </div>
</template>
<script>
import { computed, onMounted, reactive, toRefs } from "vue";
import { useStore } from "vuex";
import { encrypt, sumByCol } from "@/utils/helper";
import DefaultButton from "@/components/button/DefaultButton.vue";
import TicketSummary from "./TicketSummary.vue";
import StripeService from "@/services/StripeService";
import { ReservationStatus } from "@/utils/enum";
import { useRoute } from "vue-router";
import { useMeta } from "vue-meta";
import Swal from "sweetalert2";
import { useGtag } from "vue-gtag-next";

import Dinero from "dinero.js";
export default {
  components: { DefaultButton, TicketSummary },
  name: "OrderSummary",
  setup() {
    const store = useStore();
    const route = useRoute();
    const { pageview } = useGtag();
    const state = reactive({
      isLoading: false,
      agree: false,
      currentUser: computed(() => store.state.auth.currentUser),
      selectedSeats: computed(() => store.getters["reservation/selectedSeats"]),
      sumByCol: computed(() => (obj, col) => {
        return sumByCol(obj, col);
      }),
      reservation: computed(() => store.state.reservation.reservation),
      reservationID: computed(() => store.state.reservation.reservationID),
      show: computed(() => store.state.show.show),
      event: computed(() => store.state.event.event),
      paymentTypes: computed(() => store.getters["show/paymentTypes"]),
    });

    useMeta(
      computed(() => ({
        title: (state.event?.title ?? "Event") + " - Order Summary",
      }))
    );

    onMounted(() => {
      if (!state.show.id) {
        store.dispatch("show/findShow", route.params.showSlug).then(() => {
          // store.dispatch("show/fetchShowSeats");
        });
        // store.dispatch("event/findEvent", route.params.showSlug).then(() => {
        //   store.dispatch("show/findShow", route.params.showId).then(() => {
        //     store.dispatch("show/fetchShowSeats");
        //   });
        // });
      }
    });

    function handlePayment() {
      const metadata = {
        id: state.reservation.id,
        event: state.event.title,
        userName: state.reservation.name,
        userEmail: state.currentUser.email,
        userPhone: state.reservation.phone,
        userCity: state.reservation.city,
        eventCity: state.show.city,
        promoterId: state.reservation.promoterId,
        promoterName: state.reservation.promoterName,
        screening: state.show.id,
        seats: JSON.stringify(state.reservation.seats.map((a) => a.id)),
        tickets: state.reservation.tickets,
      };
      const lineItems = [];

      let total = Dinero({ amount: 0, currency: state.show.paymentCurrency });
      state.reservation.seats.map((seat) => {
        const ticketTotal = Dinero({
          amount: seat.price * 100,
          currency: state.show.paymentCurrency,
        });
        total = total.add(ticketTotal);

        lineItems.push({
          price_data: {
            currency: state.show.paymentCurrency,
            product_data: {
              name: seat.label,
              metadata: {
                category: seat.category,
                id: seat.id,
              },
            },
            unit_amount: ticketTotal.getAmount(),
          },
          quantity: 1,
        });
      });

      if (state.show.fees?.percentage) {
        const subTotal = total;
        const serviceCharge = total
          .add(
            Dinero({
              amount: state.show?.fees.stripeTransactionFee,
              currency: state.show.paymentCurrency,
            })
          )
          .divide((100 - state.show?.fees.percentage) / 100)
          .subtract(subTotal);

        lineItems.push({
          price_data: {
            currency: state.show.paymentCurrency,
            product_data: {
              name: "Service Charge",
            },
            unit_amount: serviceCharge.getAmount(),
          },
          quantity: 1,
        });
      }

      const failToken = encrypt(
        JSON.stringify({
          status: "fail",
          resId: state.reservation.id,
        })
      );

      const successToken = encrypt(
        JSON.stringify({
          status: "success",
          resId: state.reservation.id,
        })
      );

      // ! Create stripe session
      state.isLoading = true;
      StripeService.createSession({
        cancel_url: `${process.env.VUE_APP_URL}/${route.params.showSlug}/payment?t=${failToken}`,
        success_url: `${process.env.VUE_APP_URL}/${route.params.showSlug}/payment?t=${successToken}`,
        lineItems,
        paymentTypes: state.paymentTypes,
        metadata: metadata,
      })
        .then(({ data }) => {
          store.dispatch("reservation/setStripeSession", {
            url: data.session,
            time: new Date(),
          });
          store
            .dispatch("reservation/setStatus", {
              state: ReservationStatus.CHECKOUT,
            })
            .then(() => {
              if (process.env.VUE_APP_GOOGLE_ANALYTICS) {
                pageview({
                  page_path: location.pathname,
                  page_location: location.href,
                  page_title: `${state.event.title} - Stripe checkout`,
                });
              }

              state.isLoading = false;
              location.assign(data.session);
            })
            .catch(() => {
              state.isLoading = false;
            });
        })
        .catch((e) => {
          console.log(e);
          state.isLoading = false;
        });
    }

    function handleTermsAndCondition() {
      if (!state.show.terms) {
        window.open("/terms", "_blank");
        return;
      }

      const link = state.show.terms.link
        ? `<br>Click <a href="${state.show.terms.link}" target="_blank" class="text-[#2563eb] mt-5">here</a>  for additional information about the terms and conditions.`
        : "";

      Swal.fire({
        title: "Terms and Condition",
        html: state.show.terms.text + link,
        showConfirmButton: true,
        allowOutsideClick: false,
      });
    }

    return {
      ...toRefs(state),
      handlePayment,
      handleTermsAndCondition,
    };
  },
};
</script>
<style lang="scss">
.n3-checkbox {
  display: inline-block;
  position: relative;
  padding-left: 35px;
  margin-bottom: 12px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.n3-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 25px;
  width: 25px;
  border: 2px solid;
  border-color: #ddd;
  border-radius: 5px;
}

.n3-checkbox:hover input ~ .checkmark {
  border-color: theme("colors.n3yellow");
}

.n3-checkbox input:checked ~ .checkmark {
  border-color: theme("colors.n3yellow");
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.n3-checkbox input:checked ~ .checkmark:after {
  display: block;
}

.n3-checkbox .checkmark:after {
  left: 12px;
  top: -5px;
  width: 8px;
  height: 21px;
  border: solid theme("colors.n3yellow");
  border-width: 0 3px 3px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
</style>
